import { Component, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import Swal from 'sweetalert2';
import {
  MaintenanceService,
  MaintenanceStatusDto,
  MaintenanceAnalyticsDto,
  MaintenanceRecordDto
} from './maintenance.service';

@Component({
  selector: 'app-maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss']
})
export class MaintenanceComponent implements OnInit {
  displayedColumns: string[] = [
    'vehiclePlate',
    'model',
    'totalDistance',
    'totalDeliveries',
    'daysSinceLastMaintenance',
    'status',
    'trend',
    'lastMaintenanceDate',
    'actions'
  ];

  recordsColumns: string[] = ['vehiclePlate', 'recordType', 'description', 'cost', 'serviceDate'];

  dataSource = new MatTableDataSource<MaintenanceStatusDto>([]);
  recordsSource = new MatTableDataSource<MaintenanceRecordDto>([]);
  meterLogs: any[] = [];
  phoneLogs: any[] = [];
  raw: MaintenanceStatusDto[] = [];
  analytics: MaintenanceAnalyticsDto | null = null;

  loading = false;
  text = '';
  status: 'all' | 'needs' | 'ok' = 'all';
  totalVehicles = 0;
  needsCount = 0;
  avgDays = 0;
  maxDays = 1;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private maintenance: MaintenanceService) {}

  ngOnInit(): void {
    this.runCheck();
  }

  runCheck(): void {
    this.loading = true;
    forkJoin({
      status: this.maintenance.check(),
      records: this.maintenance.getRecords(),
      analytics: this.maintenance.getAnalytics(),
      meters: this.maintenance.getMeterLogs(),
      phones: this.maintenance.getPhoneLogs()
    }).subscribe({
      next: ({ status, records, analytics, meters, phones }) => {
        this.raw = status || [];
        this.computeKpis();
        this.bind(this.raw);
        this.recordsSource.data = records || [];
        this.analytics = analytics;
        this.meterLogs = meters || [];
        this.phoneLogs = phones || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error('[Maintenance] ❌ Check failed:', err);
        Swal.fire('Error', 'Failed to load maintenance status.', 'error');
      }
    });
  }

  async addRecord(): Promise<void> {
    const vehicleOptions = this.raw
      .map(v => `<option value="${v.vehicleId}">${v.vehiclePlate} - ${v.model}</option>`)
      .join('');

    const result = await Swal.fire({
      title: 'Add Service/Repair Record',
      html: `
        <select id="rec-vehicle" class="swal2-input">${vehicleOptions}</select>
        <select id="rec-type" class="swal2-input">
          <option value="Service">Service</option>
          <option value="Repair">Repair</option>
        </select>
        <input id="rec-desc" class="swal2-input" placeholder="Description">
        <input id="rec-cost" class="swal2-input" type="number" step="0.01" min="0" placeholder="Cost">
        <input id="rec-odo" class="swal2-input" type="number" step="0.01" min="0" placeholder="Odometer (optional)">
      `,
      showCancelButton: true,
      confirmButtonText: 'Save Record',
      preConfirm: () => {
        const vehicleId = Number((document.getElementById('rec-vehicle') as HTMLSelectElement)?.value);
        const recordType = (document.getElementById('rec-type') as HTMLSelectElement)?.value;
        const description = (document.getElementById('rec-desc') as HTMLInputElement)?.value?.trim();
        const cost = Number((document.getElementById('rec-cost') as HTMLInputElement)?.value);
        const odoRaw = (document.getElementById('rec-odo') as HTMLInputElement)?.value;

        if (!vehicleId) {
          Swal.showValidationMessage('Select a vehicle.');
          return false;
        }
        if (!description) {
          Swal.showValidationMessage('Description is required.');
          return false;
        }
        if (Number.isNaN(cost) || cost < 0) {
          Swal.showValidationMessage('Cost must be valid.');
          return false;
        }
        const payload: any = { vehicleId, recordType, description, cost };
        if (odoRaw !== '') payload.odometer = Number(odoRaw);
        return payload;
      }
    });

    if (!result.isConfirmed) return;

    this.maintenance.addRecord(result.value).subscribe({
      next: () => {
        Swal.fire('Saved', 'Maintenance record added.', 'success');
        this.runCheck();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to add maintenance record.', 'error')
    });
  }

  bind(data: MaintenanceStatusDto[]) {
    this.dataSource = new MatTableDataSource(data);
    if (this.paginator) this.dataSource.paginator = this.paginator;
    if (this.sort) this.dataSource.sort = this.sort;
  }

  computeKpis() {
    this.totalVehicles = this.raw.length;
    this.needsCount = this.raw.filter(v => v.needsMaintenance).length;
    this.avgDays = this.raw.length
      ? Math.round(this.raw.reduce((a, b) => a + (b.daysSinceLastMaintenance || 0), 0) / this.raw.length)
      : 0;

    this.maxDays = Math.max(
      1,
      ...this.raw.map(v => v.daysSinceLastMaintenance || 0)
    );
  }

  applyTextFilter(ev: Event) {
    this.text = (ev.target as HTMLInputElement).value?.trim().toLowerCase();
    this.applyFilters();
  }

  applyStatusFilter(value: 'all' | 'needs' | 'ok') {
    this.status = value;
    this.applyFilters();
  }

  resetFilters() {
    this.text = '';
    this.status = 'all';
    this.applyFilters();
  }

  applyFilters() {
    let data = [...this.raw];

    if (this.text) {
      data = data.filter(d =>
        (d.vehiclePlate || '').toLowerCase().includes(this.text) ||
        (d.model || '').toLowerCase().includes(this.text)
      );
    }

    if (this.status === 'needs') data = data.filter(d => d.needsMaintenance);
    if (this.status === 'ok') data = data.filter(d => !d.needsMaintenance);

    this.bind(data);
  }

  markAsMaintained(row: MaintenanceStatusDto) {
    Swal.fire({
      title: 'Confirm Maintenance',
      text: `Mark ${row.vehiclePlate} as maintained?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, record',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.loading = true;
      this.maintenance.markDone(row.vehicleId).subscribe({
        next: () => {
          Swal.fire('Saved', 'Vehicle maintenance recorded successfully.', 'success');
          this.runCheck();
        },
        error: (err) => {
          this.loading = false;
          console.error('[Maintenance] ❌ Mark done failed:', err);
          Swal.fire('Error', 'Failed to record maintenance.', 'error');
        }
      });
    });
  }

  barWidth(days: number): string {
    const pct = Math.min(100, Math.round((days / this.maxDays) * 100));
    return `${pct}%`;
  }

  formatDistance(val?: number): string {
    return `${(val || 0).toFixed(1)} km`;
  }
}
